import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Ikon, MetinAlani, Rozet, Secim, cn } from "@koclukpanel/ui";
import {
  ApiHatasi,
  mazeretIsaretle,
  programToplu,
  programSablonKaydet,
  programSablonUygula,
  programSablonlari,
  type ProgramSablonu,
  type TopluDegerler,
} from "./api";
import {
  BOS_HIZLI_GIRIS,
  hizliGirisDegerleri,
  hizliGirisHatalari,
  kapsamGovdesi,
  type HizliGirisFormu,
  type HizliGirisKapsami,
} from "./hizli-giris";

/**
 * PROGRAM HAZIRLAMA ARAÇLARI — F-SCH-04/05/09
 *
 * ================================================================
 * ÜÇÜ DE SUNUCUDA VARDI, PANELDE HİÇBİRİ YOKTU
 * ================================================================
 * Program ekranı bugüne kadar yalnızca "görev ekle / sil / önceki
 * haftayı kopyala" yapabiliyordu. Şartnamenin koçun zamanını
 * gerçekten kurtaran üç maddesi arayüzsüzdü:
 *
 *   F-SCH-09 mazeret  — tatil günü tamamlama oranının PAYDASINDAN düşer
 *   F-SCH-04 hızlı giriş — bir değeri seçili satıra / güne / tüm haftaya uygular
 *   F-SCH-05 şablon   — haftayı kaydet, başka haftaya uygula
 *
 * ================================================================
 * NEDEN AYRI DOSYA
 * ================================================================
 * `Program.tsx` zaten 580 satır ve haftalık ızgarayı çiziyor. Bu üç
 * araç oraya gömülseydi dosya okunmaz hale gelirdi; hepsi de programın
 * KİMLİĞİNDEN başka bir şeye bağlı değil.
 */

const GUN_ADLARI = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

/** `YYYY-AA-GG` — sunucu `z.coerce.date()` ile alıyor. */
function gunISO(haftaBasi: string, kayma: number): string {
  const d = new Date(haftaBasi);
  d.setUTCDate(d.getUTCDate() + kayma);
  return d.toISOString().slice(0, 10);
}

export type HazirlamaAraci = "mazeret" | "toplu" | "sablon";

export function ProgramAraclari({
  programId,
  haftaBasi,
  mazeretliGunler,
  onDegisti,
  kilitli,
  seciliIdler = [],
  gorevGunleri = [],
  onSecimiTemizle,
  acik,
  onAcik,
}: {
  programId: string;
  /** Haftanın pazartesi günü — `YYYY-AA-GG` */
  haftaBasi: string;
  /** Sunucudan gelen mazeretli tarihler (`YYYY-AA-GG`) */
  mazeretliGunler: readonly string[];
  onDegisti: () => void;
  /**
   * Arşivlenmiş programda araçlar kapalı.
   *
   * YAYINDA olan program kilitli DEĞİL: yayın sonrası değişiklik
   * yasak değil, revizyon üretiyor (F-SCH-14) ve ekran bunu zaten
   * ayrıca uyarıyor. Kilitlemek, "öğrenci hastalandı" gibi gerçek bir
   * durumda koçu çaresiz bırakırdı.
   */
  kilitli: boolean;
  /** Program tablosunda işaretlenen görevler — "Seçili satıra uygula" (F-SCH-04b). */
  seciliIdler?: readonly string[];
  /** Haftadaki her görevin günü (`YYYY-AA-GG`); onayda "kaç görev değişecek" için. */
  gorevGunleri?: readonly string[];
  onSecimiTemizle?: () => void;
  /**
   * Açık araç — DURUM ÇAĞIRANDA, TEK YERDE (bağımsız inceleme, 05.10.2026).
   * Önce burada ayrı bir `useState` ve program ekranında "hızlı giriş açık
   * mı" diye ikinci bir kopya vardı; önbellekte olmayan haftaya geçince
   * bileşen yeniden kuruldu, kopya `true` kaldı ve KAPALI panelin altında
   * onay kutuları çizildi. Tablodaki kutular bu değere bakıyor.
   */
  acik: HazirlamaAraci | null;
  onAcik: (arac: HazirlamaAraci | null) => void;
}) {
  if (kilitli) return null;

  /*
    UNİKOÇ "Hızlı Giriş" kartı (sürüm 9a, EditSchedule ekranı): eski panelde
    kart HEP açıktı. Burada da açık başlıyor (varsayılanı program ekranı
    veriyor), ama kapatılabiliyor: tatil/mazeret ve şablon aynı kartın öbür
    sekmeleri — eski panelde olmayan iki özellik, ayrı bir kart açmak yerine
    buraya sekme olarak giriyor.
  */
  return (
    <section
      aria-label="Hazırlama araçları"
      className="bg-notr-0 border-mor-kenar flex flex-col gap-4 rounded-[1.25rem] border p-4 shadow-(--golge-unk-kart) md:p-5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-metin-koyu mr-auto text-[0.9375rem] font-bold">
          {acik === "mazeret" ? "Tatil / mazeret" : acik === "sablon" ? "Şablon" : "Hızlı Giriş"}
        </h2>
        {(
          [
            ["toplu", "Hızlı giriş"],
            ["mazeret", "Tatil / mazeret"],
            ["sablon", "Şablon"],
          ] as const
        ).map(([d, etiket]) => (
          <button
            key={d}
            type="button"
            // `aria-expanded`, `aria-pressed` değil: bu bir süzgeç değil, altında
            // panel AÇAN bir düğme; ekran okuyucu açıldığını bununla biliyor.
            aria-expanded={acik === d}
            onClick={() => onAcik(acik === d ? null : d)}
            className={cn(
              "min-h-dokunma rounded-rozet px-4 text-sm font-semibold",
              acik === d
                ? "text-notr-0 bg-(image:--gradyan-unk-birincil)"
                : "bg-notr-0 text-metin-koyu ring-mor-kenar hover:bg-mor-50 ring-1",
            )}
          >
            {etiket}
          </button>
        ))}
      </div>

      {acik === "mazeret" && (
        <Mazeret
          programId={programId}
          haftaBasi={haftaBasi}
          mazeretliGunler={mazeretliGunler}
          onDegisti={onDegisti}
        />
      )}
      {acik === "toplu" && (
        <HizliGiris
          programId={programId}
          haftaBasi={haftaBasi}
          onDegisti={onDegisti}
          seciliIdler={seciliIdler}
          gorevGunleri={gorevGunleri}
          onSecimiTemizle={onSecimiTemizle}
        />
      )}
      {acik === "sablon" && <Sablonlar programId={programId} onDegisti={onDegisti} />}
    </section>
  );
}

/*
  UNİKOÇ toplu uygula düğmeleri: mavi #22BBFF, yeşil #4EC02C, kırmızı-pembe #FB3D6D (tarama-2 §2.9/6).
  Ölçülen renklerin kendi belirteçleri var (`eylem-mavi/yesil/kirmizi`). Beyaz yazı bunlarda AA
  altında (2,18 / 2,36 / 3,51) — eski panelde de böyleydi; tasarım birebir, menü yazısındaki kararla aynı.
*/
const RENKLI: Record<"mavi" | "yesil" | "kirmizi", string> = {
  mavi: "bg-eylem-mavi",
  yesil: "bg-eylem-yesil",
  kirmizi: "bg-eylem-kirmizi",
};

function RenkliDugme({
  renk,
  yukleniyor,
  disabled,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  renk: keyof typeof RENKLI;
  yukleniyor?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || yukleniyor}
      aria-busy={yukleniyor || undefined}
      className={cn(
        "min-h-dokunma text-notr-0 rounded-lg px-4 text-sm font-semibold",
        "enabled:hover:shadow-yukselen transition-shadow disabled:cursor-not-allowed disabled:opacity-50",
        "focus-visible:outline-marka-600 focus-visible:outline-2 focus-visible:outline-offset-2",
        RENKLI[renk],
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------

function Mazeret({
  programId,
  haftaBasi,
  mazeretliGunler,
  onDegisti,
}: {
  programId: string;
  haftaBasi: string;
  mazeretliGunler: readonly string[];
  onDegisti: () => void;
}) {
  const isaretle = useMutation({
    mutationFn: (v: { tarih: string; mazeretli: boolean }) =>
      mazeretIsaretle(programId, v.tarih, v.mazeretli),
    onSuccess: onDegisti,
  });

  return (
    <div className="flex flex-col gap-3">
      {/*
        NEDEN ÖNEMLİ, AÇIKÇA YAZILI. "Mazeretli" kelimesi tek başına
        ne yaptığını söylemiyor; koç işaretlemezse tatil günü öğrenciyi
        "yapmadı" diye gösteriyor ve haftalık oran sebepsiz düşüyor.
      */}
      <p className="text-notr-600 text-sm">
        İşaretlenen gün tamamlama oranının <strong>paydasından düşer</strong> — o gün yapılacak iş
        olmadığı için öğrenci &quot;eksik&quot; görünmez{/* F-SCH-09 */}.
      </p>

      <ul className="flex flex-wrap gap-2">
        {GUN_ADLARI.map((ad, i) => {
          const tarih = gunISO(haftaBasi, i);
          const mazeretli = mazeretliGunler.includes(tarih);
          return (
            <li key={tarih}>
              <Buton
                gorunum={mazeretli ? "birincil" : "ikincil"}
                yukleniyor={isaretle.isPending && isaretle.variables?.tarih === tarih}
                onClick={() => isaretle.mutate({ tarih, mazeretli: !mazeretli })}
              >
                {ad}
                {mazeretli && " ✓"}
              </Buton>
            </li>
          );
        })}
      </ul>

      {isaretle.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {isaretle.error instanceof ApiHatasi ? isaretle.error.message : "İşaretlenemedi."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

/*
  `<fieldset>` + `float-left` lejant: lejant kenarlığın ÜSTÜNE oturmasın, eski
  paneldeki gibi kartın içinde başlık olsun. `min-w-0` ŞART — fieldset'in
  tarayıcı varsayılanı `min-inline-size: min-content`; uzun bağlantı
  yapıştırılınca kart 390 px'de taşıyordu.
*/
const ALT_KART =
  "bg-mor-50 border-mor-kenar flex min-w-0 flex-col gap-2 rounded-2xl border p-3 [&>legend+*]:clear-left";
const ALT_KART_BASLIGI = "text-metin-koyu float-left mb-1 w-full text-[0.9375rem] font-bold";

/**
 * HIZLI GİRİŞ — F-SCH-04 / F-SCH-04b (müşteri şablonu, Emre revizesi 04.10.2026)
 *
 * Şablonun üç grubu ve üç düğmesi. Görünüm müşterinin mor tasarımına değil
 * panelin diline uyarlandı (Emre, 04.10).
 *
 * ================================================================
 * "TÜM SATIRLAR" ONAY İSTİYOR, DİĞER İKİSİ İSTEMİYOR
 * ================================================================
 * Seçili satır ve gün kapsamı koçun gözünün önünde: neyi değiştireceğini
 * tabloda görüyor. Tüm hafta ise ekranın dışına taşan satırları da
 * değiştiriyor ve geri alma yok; kaç görevin değişeceğini SÖYLEMEDEN
 * uygulamak, yanlış alana yazılmış bir bağlantıyı haftaya yaymak demek.
 */
function HizliGiris({
  programId,
  haftaBasi,
  onDegisti,
  seciliIdler,
  gorevGunleri,
  onSecimiTemizle,
}: {
  programId: string;
  haftaBasi: string;
  onDegisti: () => void;
  seciliIdler: readonly string[];
  gorevGunleri: readonly string[];
  onSecimiTemizle?: () => void;
}) {
  const [form, setForm] = useState<HizliGirisFormu>(BOS_HIZLI_GIRIS);
  // Varsayılan gün: haftada görevi olan ilk gün; yoksa pazartesi.
  const [gun, setGun] = useState<string>(() => {
    const gunler = GUN_ADLARI.map((_, i) => gunISO(haftaBasi, i));
    return gunler.find((g) => gorevGunleri.includes(g)) ?? gunler[0] ?? haftaBasi;
  });
  const [onayBekliyor, setOnayBekliyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ etkilenen: number; kapsam: string } | null>(null);

  const yaz = (k: keyof HizliGirisFormu, v: string) => {
    setForm({ ...form, [k]: v });
    setSonuc(null);
  };

  const hatalar = hizliGirisHatalari(form);
  const hataVar = Object.keys(hatalar).length > 0;
  const degerler = hizliGirisDegerleri(form);
  const bosDeger = Object.keys(degerler).length === 0;
  const uygulanamaz = bosDeger || hataVar;

  const gunAdi = GUN_ADLARI[GUN_ADLARI.findIndex((_, i) => gunISO(haftaBasi, i) === gun)] ?? gun;
  const gunGorevSayisi = gorevGunleri.filter((g) => g === gun).length;
  const haftaGorevSayisi = gorevGunleri.length;

  /*
    DEĞERLER İSTEK ANINDA SABİTLENİYOR. "Tüm satırlar" onayı açıkken form
    düzenlenebiliyordu (bağımsız inceleme, 05.10.2026): koç onay metnini
    okurken bir alanı değiştirse, onayladığı değil o anki değer haftaya
    yazılırdı. Onay penceresi açılınca değerler dondurulup form
    kilitleniyor; istek o dondurulmuş değerleri gönderiyor.
  */
  const [donmus, setDonmus] = useState<TopluDegerler | null>(null);

  /*
    ODAK ONAYA GİDER, KAPANINCA TETİKLEYENE DÖNER (2. tur inceleme).
    Onay penceresi düğme satırının ALTINDA açılıyor; klavye ve ekran
    okuyucu kullanıcısı açıldığını fark etmiyordu. Kapanınca da odak
    kaybolup sayfanın başına düşüyordu. Tetikleyen düğme onay sırasında
    kapalı olduğu için odak, kilit kalktıktan SONRA (efektte) veriliyor.
  */
  const onayRef = useRef<HTMLDivElement>(null);
  // `Buton` ref almıyor; tetikleyen düğme bu kapsayıcının içinden bulunuyor.
  const tetikleyiciRef = useRef<HTMLSpanElement>(null);
  const oncekiOnay = useRef(false);
  useEffect(() => {
    if (onayBekliyor) onayRef.current?.focus();
    else if (oncekiOnay.current) tetikleyiciRef.current?.querySelector("button")?.focus();
    oncekiOnay.current = onayBekliyor;
  }, [onayBekliyor]);
  const onayiKapat = () => {
    setOnayBekliyor(false);
    setDonmus(null);
  };

  const uygula = useMutation({
    mutationFn: (v: { kapsam: HizliGirisKapsami; etiket: string; degerler: TopluDegerler }) =>
      programToplu(programId, kapsamGovdesi(v.kapsam), v.degerler),
    onSuccess: (s, v) => {
      setSonuc({ etkilenen: s.etkilenen, kapsam: v.etiket });
      setOnayBekliyor(false);
      setDonmus(null);
      onDegisti();
    },
  });

  const calistir = (kapsam: HizliGirisKapsami, etiket: string, d: TopluDegerler = degerler) => {
    setSonuc(null);
    uygula.mutate({ kapsam, etiket, degerler: d });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-notr-600 text-sm">
        Girilen değerler <strong>mevcut görevlere</strong> yazılır; yeni görev oluşturmaz. Ders ve
        konu tablodaki satırdan seçilir. <strong>Boş bırakılan alan değişmez.</strong>
      </p>

      {/*
        ÜÇ GRUP, ŞABLONDAKİ SIRAYLA. `min-w-0` ŞART: `<fieldset>`in tarayıcı
        varsayılanı `min-inline-size: min-content`; uzun bir bağlantı
        yapıştırılınca kutu 390 px'de ekrandan taşıyordu (görev formunda
        ölçülmüştü, aynı tuzak).
      */}
      {/* Onay beklerken alanlar kilitli; `contents` yerleşimi değiştirmiyor. */}
      <fieldset disabled={onayBekliyor} className="contents">
        {/*
          DÖRT ALT KART, ESKİ PANELDEKİ SIRAYLA: ilki yalnız bilgi — ders, konu
          ve kaynak toplu yazılamıyor (her satırın kendi müfredat seçimi), eski
          panel de bunu aynı cümleyle söylüyordu.
        */}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="bg-mor-50 border-mor-kenar flex min-w-0 flex-col gap-2 rounded-2xl border p-3">
            <h3 className="text-metin-koyu text-[0.9375rem] font-bold">Ders / Konu / Kaynak</h3>
            <p className="text-metin-ikincil text-sm font-semibold">
              Ders – Konu – Kaynak (tablodan seçiniz)
            </p>
            <p className="text-metin-ikincil text-xs">
              Bu bölüm bilgilendirme amaçlıdır. Seçimler tablodaki satırlardan yapılır.
            </p>
          </div>
          <fieldset className={ALT_KART}>
            <legend className={ALT_KART_BASLIGI}>Soru / Test</legend>
            <div className="grid grid-cols-2 gap-2">
              <Alan
                etiket="Soru sayısı"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.soru}
                onChange={(e) => yaz("soru", e.target.value)}
                hata={hatalar.soru}
              />
              <Alan
                etiket="Test sayısı"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.test}
                onChange={(e) => yaz("test", e.target.value)}
                hata={hatalar.test}
              />
            </div>
          </fieldset>

          <fieldset className={ALT_KART}>
            <legend className={ALT_KART_BASLIGI}>Video</legend>
            <Alan
              etiket="YouTube linki"
              type="url"
              inputMode="url"
              placeholder="https://youtu.be/…"
              value={form.youtube}
              onChange={(e) => yaz("youtube", e.target.value)}
              hata={hatalar.youtube}
            />
            <Alan
              etiket="Video başlığı"
              value={form.videoBasligi}
              maxLength={200}
              onChange={(e) => yaz("videoBasligi", e.target.value)}
              hata={hatalar.videoBasligi}
            />
          </fieldset>

          <fieldset className={ALT_KART}>
            <legend className={ALT_KART_BASLIGI}>PDF</legend>
            <Alan
              etiket="Konu PDF"
              type="url"
              inputMode="url"
              placeholder="https://drive.google.com/…"
              value={form.konuPdf}
              onChange={(e) => yaz("konuPdf", e.target.value)}
              hata={hatalar.konuPdf}
            />
            <Alan
              etiket="Test PDF"
              type="url"
              inputMode="url"
              placeholder="https://drive.google.com/…"
              value={form.testPdf}
              onChange={(e) => yaz("testPdf", e.target.value)}
              hata={hatalar.testPdf}
            />
          </fieldset>
        </div>

        <MetinAlani
          etiket="Koç notu"
          rows={2}
          value={form.not}
          onChange={(e) => yaz("not", e.target.value)}
          hata={hatalar.not}
        />
      </fieldset>

      {uygula.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {uygula.error instanceof ApiHatasi ? uygula.error.message : "Uygulanamadı."}
        </p>
      )}

      {sonuc !== null && !uygula.isError && (
        <p role="status" className="text-notr-700 flex items-center gap-2 text-sm">
          <Ikon ad="tamam" className="text-onay-600" />
          {/*
            SIFIR DA BİR CEVAP. "0 görev güncellendi" demek, sessizce
            başarılı görünmekten iyi: seçilen günde görev yoksa koç
            bunu bilmeli, yoksa değerin uygulandığını sanır.
          */}
          {sonuc.etkilenen === 0
            ? `${sonuc.kapsam}: bu kapsamda görev yok — hiçbir şey değişmedi.`
            : `${sonuc.kapsam}: ${sonuc.etkilenen} görev güncellendi.`}
        </p>
      )}

      {/*
        NEDEN KAPALI OLDUĞU YAZILI. Yalnızca soluk bir düğme, koça neyin
        eksik olduğunu söylemiyor; "bozuk mu?" diye düşünüp tekrar tekrar
        basıyor.
      */}
      {uygulanamaz && (
        <p className="text-notr-600 text-xs">
          {hataVar ? "Hatalı alanları düzeltin." : "Uygulamak için en az bir alan doldurun."}
        </p>
      )}

      {/*
        Onay açıkken DİĞER KAPSAMLAR da kapalı: koç onay metnini okurken
        "Güne uygula"ya basabiliyor ya da günü değiştirebiliyordu; onaylanan
        şey ile ekranda olan şey ayrışıyordu.
      */}
      <fieldset disabled={onayBekliyor} className="contents">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start">
          <div className="flex flex-col gap-1">
            <RenkliDugme
              renk="mavi"
              disabled={uygulanamaz || seciliIdler.length === 0}
              yukleniyor={uygula.isPending && uygula.variables?.kapsam.tur === "secili"}
              onClick={() =>
                calistir({ tur: "secili", gorevIdler: seciliIdler }, "Seçili satırlar")
              }
            >
              Seçili Satıra Uygula{seciliIdler.length > 0 ? ` (${seciliIdler.length})` : ""}
            </RenkliDugme>
            {seciliIdler.length === 0 ? (
              <span className="text-metin-ikincil text-xs">
                Tablodan görev seçin (satırdaki onay kutusu).
              </span>
            ) : (
              onSecimiTemizle && (
                <button
                  type="button"
                  onClick={onSecimiTemizle}
                  className="text-mor-700 min-h-dokunma self-start text-xs font-medium underline-offset-2 hover:underline"
                >
                  Seçimi temizle
                </button>
              )
            )}
          </div>

          {/*
            GÜN SEÇİCİ eski panelde yoktu (orada "gün" seçili satırın günüydü);
            burada seçili satır olmadan da bir güne uygulanabiliyor, hangi güne
            gideceği yazılı olmalı.
          */}
          <div className="flex items-end gap-2">
            <Secim etiket="Gün" value={gun} onChange={(e) => setGun(e.target.value)}>
              {GUN_ADLARI.map((ad, i) => (
                <option key={ad} value={gunISO(haftaBasi, i)}>
                  {ad}
                </option>
              ))}
            </Secim>
            <RenkliDugme
              renk="yesil"
              disabled={uygulanamaz}
              yukleniyor={uygula.isPending && uygula.variables?.kapsam.tur === "gun"}
              onClick={() => calistir({ tur: "gun", tarih: gun }, gunAdi)}
            >
              Güne Uygula{gunGorevSayisi > 0 ? ` (${gunGorevSayisi})` : ""}
            </RenkliDugme>
          </div>

          <span ref={tetikleyiciRef} className="contents">
            <RenkliDugme
              renk="kirmizi"
              disabled={uygulanamaz || onayBekliyor}
              aria-expanded={onayBekliyor}
              onClick={() => {
                setSonuc(null);
                setDonmus(degerler);
                setOnayBekliyor(true);
              }}
            >
              Tüm Satırlara Uygula
            </RenkliDugme>
          </span>
        </div>
      </fieldset>

      {onayBekliyor && (
        <div
          ref={onayRef}
          tabIndex={-1}
          role="alertdialog"
          aria-label="Tüm satırlara uygulama onayı"
          onKeyDown={(e) => {
            if (e.key === "Escape") onayiKapat();
          }}
          className="focus-visible:outline-marka-600 rounded-alan border-uyari-200 bg-uyari-50 flex flex-col gap-3 border p-3 focus-visible:outline-2"
        >
          <p className="text-notr-800 text-sm">
            {haftaGorevSayisi === 0 ? (
              "Bu haftada görev yok — uygulanacak bir şey yok."
            ) : (
              <>
                Haftadaki <strong>{haftaGorevSayisi} görevin tamamı</strong> değişecek. Girilen
                alanlar bu görevlerdeki mevcut değerlerin üzerine yazılır.
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Buton
              disabled={haftaGorevSayisi === 0}
              yukleniyor={uygula.isPending && uygula.variables?.kapsam.tur === "hafta"}
              onClick={() => calistir({ tur: "hafta" }, "Bütün hafta", donmus ?? degerler)}
            >
              Evet, {haftaGorevSayisi} göreve uygula
            </Buton>
            <Buton gorunum="sessiz" onClick={onayiKapat}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function Sablonlar({ programId, onDegisti }: { programId: string; onDegisti: () => void }) {
  const qc = useQueryClient();
  const [ad, setAd] = useState("");
  const [paylasimli, setPaylasimli] = useState(false);
  const [uygulanan, setUygulanan] = useState<number | null>(null);

  const liste = useQuery<ProgramSablonu[]>({
    queryKey: ["program-sablonlari"],
    queryFn: programSablonlari,
    retry: false,
  });

  const kaydet = useMutation({
    mutationFn: () => programSablonKaydet(programId, ad.trim(), paylasimli),
    onSuccess: async () => {
      setAd("");
      setPaylasimli(false);
      await qc.invalidateQueries({ queryKey: ["program-sablonlari"] });
    },
  });

  const uygula = useMutation({
    mutationFn: (sablonId: string) => programSablonUygula(programId, sablonId),
    onSuccess: (s) => {
      setUygulanan((s as { eklenen?: number })?.eklenen ?? 0);
      onDegisti();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <p className="text-notr-800 text-sm font-medium">Bu haftayı şablon olarak kaydet</p>
        {/*
          ŞABLON TARİH TUTMUYOR, HAFTANIN KAÇINCI GÜNÜ bilgisini tutuyor
          (sunucu notu). Kullanıcı bunu bilmezse şablonu uyguladığında
          görevlerin neden "kaydığını" anlamaz — kaymıyor, gün numarası
          eşleşiyor.
        */}
        <p className="text-notr-600 text-xs">
          Şablon tarihleri değil, haftanın kaçıncı günü olduğunu saklar; başka bir haftaya
          uygulandığında görevler aynı günlere düşer.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Alan etiket="Şablon adı" value={ad} onChange={(e) => setAd(e.target.value)} />
          <label className="text-notr-700 flex items-end gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={paylasimli}
              onChange={(e) => setPaylasimli(e.target.checked)}
              className="ring-notr-300 size-4 rounded ring-1"
            />
            Diğer koçlar da kullanabilsin
          </label>
        </div>

        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}

        <div>
          <Buton
            gorunum="ikincil"
            yukleniyor={kaydet.isPending}
            disabled={ad.trim().length === 0}
            onClick={() => kaydet.mutate()}
          >
            Şablon olarak kaydet
          </Buton>
        </div>
      </div>

      <div className="border-notr-200 flex flex-col gap-2 border-t pt-4">
        <p className="text-notr-800 text-sm font-medium">Şablon uygula</p>

        {uygula.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {uygula.error instanceof ApiHatasi ? uygula.error.message : "Uygulanamadı."}
          </p>
        )}

        {uygulanan !== null && !uygula.isError && (
          <p className="text-notr-700 flex items-center gap-2 text-sm">
            <Ikon ad="tamam" className="text-onay-600" />
            {uygulanan} görev eklendi.
          </p>
        )}

        {liste.isPending ? (
          <p className="text-notr-600 text-sm">Yükleniyor…</p>
        ) : liste.isError ? (
          <p className="text-hata-500 text-sm">Şablonlar alınamadı.</p>
        ) : liste.data.length === 0 ? (
          <p className="text-notr-600 text-sm">
            Kayıtlı şablon yok. Bir haftayı hazırlayıp yukarıdan kaydedebilirsiniz.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {liste.data.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-notr-800 flex items-center gap-2 text-sm">
                  {s.name}
                  {s.isShared && <Rozet ton="bilgi">Paylaşımlı</Rozet>}
                </span>
                <Buton
                  gorunum="sessiz"
                  yukleniyor={uygula.isPending && uygula.variables === s.id}
                  onClick={() => uygula.mutate(s.id)}
                >
                  Bu haftaya uygula
                </Buton>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
